import React, { useEffect, useReducer, useRef, useState } from "react";

import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Endpoint from "../../../../services/api/endpoint";
import { getInitialState } from "../../../../state";
import { brandReducer } from "../../../../state/reducers";
import { IApplicationState } from "../../../../store";
import { handleChange } from "../../../../utils/editorUtils";
import { IBrand, IUser } from "../../../../utils/types/models";
import { isNullOrEmpty } from "../../../../utils/utils";
import EntityEditor, { IEditorProps, IEditScreenProps, YesNoSwitch } from "../../../global/editor";

const BrandEditor: React.FC<IEditorProps<IBrand>> = (props: IEditorProps<IBrand>) => {
	const { t } = useTranslation();
	const [state, dispatch] = useReducer(brandReducer, getInitialState<IBrand>());
	const currentUser: IUser = useSelector((reduxState: IApplicationState) => reduxState.authenticationState.currentUser);
	const initialBrand: IBrand = {
		id: (props as IEditScreenProps<IBrand>).recordId || 0,
		name: "",
		active: true,
		companyId: currentUser.currentCompanyId
	};
	const [brand, setBrand] = useState<IBrand>(initialBrand);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setBrand(state.entity);
		}
	}, [state.entity]);

	function onChange(event: React.ChangeEvent<HTMLInputElement> | SwitchChangeEvent | ComboBoxChangeEvent): void {
		setBrand(handleChange(brand, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(brand.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	const readonly: boolean = (props as IEditScreenProps<IBrand>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={brand}
			endpoint={Endpoint.Brands}
			entityState={state}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			entityType={t("brand")}
			recordName={brand.name}
			close={props.close}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={brand.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<span>{t("active")}</span>
						<YesNoSwitch name="active" checked={brand.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default BrandEditor;